import {
  API_ENDPOINT_KEYS,
  ROLLOUT_STATE,
  SERVICE_AUTH_ENDPOINTS,
} from '~common';
import { getAuthRequest } from '~services/auth.service';
import { GetRolloutConfigsResponse, RolloutConfig } from '~types';

const PREFIX = '[rollout-config]';

type RolloutStateLookup = Map<string, ROLLOUT_STATE>;

const VALID_ROLLOUT_STATES = new Set<string>(Object.values(ROLLOUT_STATE));

const isValidRolloutState = (value: unknown): value is ROLLOUT_STATE =>
  typeof value === 'string' && VALID_ROLLOUT_STATES.has(value);

const rolloutConfigManager = () => {
  const rolloutStateLookup: RolloutStateLookup = new Map<
    string,
    ROLLOUT_STATE
  >();
  // useSyncExternalStore subscribers + a monotonic snapshot token. `version`
  // changes whenever the config actually changes, so components re-render and
  // pick up new decisions instead of being stuck on a stale read.
  const listeners = new Set<() => void>();
  let version = 0;
  let focusListenerBound = false;
  let retryCount = 0; // resets to 0 on successful fetch; page reload resets the module
  let lastFetchTime = 0;
  const COOLDOWN_MS = 30_000;
  const MAX_RETRIES = 2;

  const getPermissionRolloutConfigs = (): Promise<GetRolloutConfigsResponse> =>
    getAuthRequest<GetRolloutConfigsResponse>({
      path: SERVICE_AUTH_ENDPOINTS[
        API_ENDPOINT_KEYS.GET_PERMISSION_ROLLOUT_CONFIG
      ],
    });

  const emitChange = () => {
    version += 1;
    listeners.forEach((listener) => listener());
  };

  const setConfigs = (configs: RolloutConfig[]) => {
    let changed = false;

    configs.forEach(({ permissionKey, rolloutState }) => {
      // Guard against BE/FE enum drift: an unknown state degrades to OFF
      // (legacy role check) instead of crashing the access check downstream.
      if (!isValidRolloutState(rolloutState)) {
        // eslint-disable-next-line no-console
        console.warn(
          `${PREFIX} ignoring unknown rollout state "${rolloutState}" for "${permissionKey}"`,
        );
        return;
      }
      if (rolloutStateLookup.get(permissionKey) !== rolloutState) {
        rolloutStateLookup.set(permissionKey, rolloutState);
        changed = true;
      }
    });

    // Only notify React when something actually changed, to avoid needless renders.
    if (changed) emitChange();
  };

  const getPermissionState = (permission: string): ROLLOUT_STATE => {
    const state = rolloutStateLookup.get(permission);

    if (!state)
      // eslint-disable-next-line no-console
      console.warn(
        `${PREFIX} permission "${permission}" not configured — fallback to ROLLOUT_STATE.OFF`,
      );

    return state ?? ROLLOUT_STATE.OFF;
  };

  const fetchConfig = async (reason: string) => {
    const elapsed = Date.now() - lastFetchTime;
    if (elapsed < COOLDOWN_MS) {
      // eslint-disable-next-line no-console
      console.debug(
        `${PREFIX} skipped (${reason}) — cooldown ${Math.round(
          (COOLDOWN_MS - elapsed) / 1000,
        )}s remaining`,
      );
      return;
    }
    lastFetchTime = Date.now();
    // eslint-disable-next-line no-console
    console.debug(`${PREFIX} fetching — reason: ${reason}`);
    try {
      const { data } = await getPermissionRolloutConfigs();
      setConfigs(data);
      retryCount = 0;
      // eslint-disable-next-line no-console
      console.debug(`${PREFIX} success —`, data);
    } catch (error: unknown) {
      // A failed fetch must not lock out retries for a full cooldown: clear the
      // timestamp so the next trigger (mount / focus / 403) can try again.
      lastFetchTime = 0;
      const status =
        (error as { response?: { status?: number } })?.response?.status ??
        'network error';
      // eslint-disable-next-line no-console
      console.warn(`${PREFIX} failed — reason: ${reason}, status: ${status}`);
    }
  };

  const load = (reason = 'app mount') => fetchConfig(reason);

  const onPermissionError = () => {
    const elapsed = Date.now() - lastFetchTime;
    if (elapsed < COOLDOWN_MS) {
      // eslint-disable-next-line no-console
      console.debug(
        `${PREFIX} 403 received but config is fresh (${Math.round(
          elapsed / 1000,
        )}s ago) — real permission denial, skipping refetch`,
      );
      return;
    }
    // if 403 persists after MAX_RETRIES config refetches, it's a real permission denial — stop retrying this session
    if (retryCount >= MAX_RETRIES) {
      // eslint-disable-next-line no-console
      console.warn(
        `${PREFIX} 403 received but max retries (${MAX_RETRIES}) reached — skipping refetch`,
      );
      return;
    }
    retryCount++;
    // eslint-disable-next-line no-console
    console.debug(
      `${PREFIX} 403 detected — retry ${retryCount}/${MAX_RETRIES}`,
    );
    fetchConfig(`403 retry ${retryCount}`);
  };

  // Refetch when the tab regains focus so rollout flips made elsewhere (an admin
  // toggling a permission ON) propagate without a full reload. Bound lazily on
  // first subscription so importing the module has no side effects (tests/SSR).
  const bindFocusRefetch = () => {
    if (focusListenerBound || typeof window === 'undefined') return;
    focusListenerBound = true;
    window.addEventListener('focus', () => {
      void load('window focus');
    });
  };

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    bindFocusRefetch();
    return () => {
      listeners.delete(listener);
    };
  };

  const getSnapshot = (): number => version;

  return {
    load,
    onPermissionError,
    getPermissionState,
    subscribe,
    getSnapshot,
  };
};

export const permissionRolloutConfigService = rolloutConfigManager();
